import React from "react";
import { Mail, Phone, Globe, Check } from "lucide-react";
import { PA_TAG_CLASS, VERIFIED_HINT } from "../constants/directoryOptions";
import { formatUsPhone } from "../utils/phone";
import { focusPosition } from "../utils/photoFocus";

/**
 * One directory listing, in both tiers.
 *
 * Standard listings never carry a photo or an avatar — the tier difference has
 * to be visible at a glance, and a placeholder avatar would blur it. Featured
 * listings lead with the attorney's own photo, a FEATURED LISTING badge over
 * it, and a restrained champagne border and tint.
 *
 * Contact email, phone and website stay in the standard LawFusion blue in both
 * tiers: they are the actions a consumer came for, not part of the upsell.
 */
export default function ListingCard({
  listing,
  className = "",
  id,
  // The sample card on "My Directory Listing" has to be unmistakably a sample.
  badgeLabel = "Featured Listing",
}) {
  const cityState = [listing.city, listing.state].filter(Boolean).join(", ");
  const years = (listing.yearsInPractice || "").trim();
  // Free text, so "15+" stays intact while a bare "1" still reads correctly.
  const yearsLabel = years
    ? `${years} ${years === "1" ? "Year" : "Years"} in Practice`
    : "";
  const metaLine = [cityState, yearsLabel].filter(Boolean).join(" · ");

  // A featured listing without a photo still gets the featured treatment; it
  // simply has nothing to show yet.
  const isFeatured = !!listing.featured;
  const photo = isFeatured ? listing.photoUrl : "";

  return (
    <article
      id={id}
      className={`listing-card${isFeatured ? " listing-card-featured" : ""} ${className}`.trim()}
    >
      {photo && (
        <div className="l-photo">
          <img
            src={photo}
            alt={`${listing.firstName} ${listing.lastName}`}
            // Cropping is done around the point the owner chose rather than a
            // fixed guess, so the face survives this container's shape.
            style={{ objectPosition: focusPosition(listing) }}
          />
          <span className="l-featured-badge">{badgeLabel}</span>
        </div>
      )}

      <div className="l-body">
        <div className="l-header">
          <h3 className="l-name">
            {listing.firstName} {listing.lastName}
          </h3>
          {listing.role && <span className="l-role-badge">{listing.role}</span>}
        </div>

        {isFeatured && !photo && (
          <span className="l-featured-badge l-featured-badge-inline">
            {badgeLabel}
          </span>
        )}

        {listing.firmName && <div className="l-firm">{listing.firmName}</div>}
        {metaLine && <div className="l-meta">{metaLine}</div>}

        <div className="l-tags">
          {(listing.practiceAreas || []).map((pa) => (
            <span key={pa} className={`l-tag ${PA_TAG_CLASS[pa] || "pa-other"}`}>
              {pa === "Other" && listing.practiceAreaOther
                ? listing.practiceAreaOther
                : pa}
            </span>
          ))}
        </div>

        {listing.freeConsultation && (
          <div className="l-consult">
            <Check size={13} strokeWidth={3} /> Free Consultation
          </div>
        )}

        <div className="l-links">
          {/* Icons only — a public listing should not publish a scrapable
              address and number as plain text. The value still has to be
              reachable, because a mailto: or tel: the device cannot handle
              fails silently with no event to catch: hence the tooltip on
              hover and the aria-label. On a phone the tap is the point, and
              tel:/mailto: are handled natively. The tooltip is our own
              (data-tip, see .l-link-icon in Directory.css): the native title
              only appears after a second of stillness and QA never saw it. */}
          {listing.emailPublic && (
            <a
              href={`mailto:${listing.emailPublic}`}
              className="l-link l-link-icon"
              data-tip={listing.emailPublic}
              aria-label={`Email ${listing.emailPublic}`}
            >
              <Mail size={16} aria-hidden="true" />
            </a>
          )}
          {listing.phonePublic && (
            <a
              href={`tel:${listing.phonePublic.replace(/[^\d+]/g, "")}`}
              className="l-link l-link-icon"
              data-tip={formatUsPhone(listing.phonePublic)}
              aria-label={`Call ${formatUsPhone(listing.phonePublic)}`}
            >
              <Phone size={16} aria-hidden="true" />
            </a>
          )}
          {listing.website && (
            <a
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="l-link l-link-icon"
              data-tip={listing.website}
              aria-label={`Visit website ${listing.website}`}
            >
              <Globe size={16} aria-hidden="true" />
            </a>
          )}
        </div>

        {listing.verified && (
          <div className="l-verified l-verified-row" title={VERIFIED_HINT}>
            <Check size={15} strokeWidth={3} /> Listing Verified
          </div>
        )}
      </div>
    </article>
  );
}
